import { useState, useEffect, useRef, useCallback } from "react";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";


/* ── Stable hash of all narratives combined ───────────────────────────────── */
function hashNarratives(narratives) {
    if (!narratives?.length) return "";
    try {
        const str = narratives.map(n => n.title + n.narrative).join("|");
        let h = 0;
        for (let i = 0; i < str.length; i++) {
            h = Math.imul(31, h) + str.charCodeAt(i) | 0;
        }
        return String(h >>> 0);
    } catch { return ""; }
}

/* ── Module-level summary cache: hash → summaryText ──────────────────────── */
const _summaryCache = new Map();

/* ── Inline SVG icons ────────────────────────────────────────────────────── */
const IconSparkle = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 15, height: 15, minWidth: 15, minHeight: 15, display: "block" }}>
        <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
    </svg>
);

const IconRefresh = ({ spinning }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 12, height: 12, minWidth: 12, minHeight: 12, display: "block",
                 animation: spinning ? "esb-spin 0.8s linear infinite" : "none" }}>
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
);

const IconChevron = ({ open }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 14, height: 14, minWidth: 14, minHeight: 14, display: "block",
                 transform: open ? "rotate(180deg)" : "rotate(0deg)",
                 transition: "transform 0.22s ease" }}>
        <polyline points="6 9 12 15 18 9"/>
    </svg>
);

/* ── Minimal markdown renderer ───────────────────────────────────────────── */
function parseInline(text) {
    return text
        .replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight:650;color:#0f172a">$1</strong>')
        .replace(/_(.+?)_/g, '<em>$1</em>');
}

function renderMarkdown(text) {
    if (!text) return null;
    const lines = text.split("\n");
    const elements = [];
    let bulletBuffer = [];
    let key = 0;

    const flush = () => {
        if (!bulletBuffer.length) return;
        elements.push(
            <ul key={`ul-${key++}`} style={{ listStyle: "none", padding: 0, margin: "0 0 10px", display: "flex", flexDirection: "column", gap: 5 }}>
                {bulletBuffer.map((b, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <span style={{ fontSize: 5, color: "#7c3aed", marginTop: 7, flexShrink: 0 }}>◆</span>
                        <span style={{ fontSize: 13, lineHeight: 1.65, color: "#374151", fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 420 }}
                            dangerouslySetInnerHTML={{ __html: parseInline(b) }} />
                    </li>
                ))}
            </ul>
        );
        bulletBuffer = [];
    };

    for (const raw of lines) {
        const line = raw.trim();
        if (!line) { flush(); continue; }
        if (/^[-*•]\s+/.test(line)) { bulletBuffer.push(line.replace(/^[-*•]\s+/, "")); continue; }
        flush();
        if (/^\*\*[^*]+\*\*$/.test(line)) {
            elements.push(
                <p key={key++} style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.02em", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                    {line.replace(/\*\*/g, "")}
                </p>
            );
            continue;
        }
        elements.push(
            <p key={key++} style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.7, color: "#374151", fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 420, letterSpacing: "-0.005em" }}
                dangerouslySetInnerHTML={{ __html: parseInline(line) }} />
        );
    }
    flush();
    return elements;
}

/* ── Shimmer skeleton ─────────────────────────────────────────────────────── */
const SummarySkeleton = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {[85, 100, 72, 90, 60].map((w, i) => (
            <div key={i} style={{
                height: i === 0 ? 16 : 12, width: `${w}%`, borderRadius: 6,
                background: "linear-gradient(90deg,#ede9fe 25%,#ddd6fe 50%,#ede9fe 75%)",
                backgroundSize: "200% 100%",
                animation: `esb-shimmer 1.5s ease ${i * 0.1}s infinite`,
            }} />
        ))}
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  ExecSummaryBanner                                                          */
/*                                                                             */
/*  Props:                                                                     */
/*    resolvedInsights  — array of { graphId, title, narrative|null }         */
/*    totalGraphCount   — total number of chart-type graphs expected           */
/*    tabName           — used as cache namespace                              */
/*    resetKey          — increment to force-clear cached summary              */
/*                        (e.g. after a graph is deleted)                     */
/* ═══════════════════════════════════════════════════════════════════════════ */
const ExecSummaryBanner = ({ resolvedInsights = [], totalGraphCount = 0, tabName, resetKey = 0 }) => {
    const [summary, setSummary]   = useState(null);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState(null);
    const [expanded, setExpanded] = useState(true);
    const lastHashRef             = useRef(null);
    const fetchingRef             = useRef(false);

    // Only narratives that actually have content (skip empty/null)
    const validNarratives = resolvedInsights.filter(n => !!n.narrative);

    const allResolved = totalGraphCount > 0 && resolvedInsights.length >= totalGraphCount;
    const hasEnough   = validNarratives.length >= 1;

    /* ── Reset when a graph is deleted ──────────────────────────────────────
       Parent bumps resetKey after removing a graph from resolvedInsights.
       We clear the stale cached summary so the banner re-generates with the
       reduced set of narratives rather than serving the old cached text.
    ── */
    useEffect(() => {
        if (resetKey === 0) return; // skip initial mount
        setSummary(null);
        setError(null);
        lastHashRef.current = null;
        // Evict any cached summaries that might now be stale.
        // We clear the whole map because any previously-cached summary
        // included the deleted graph's narrative — all are invalid.
        _summaryCache.clear();
    }, [resetKey]);

    const fetchSummary = useCallback(async (force = false) => {
        if (!hasEnough || fetchingRef.current) return;

        const currentHash = hashNarratives(validNarratives);

        if (!force && _summaryCache.has(currentHash)) {
            setSummary(_summaryCache.get(currentHash));
            lastHashRef.current = currentHash;
            return;
        }

        fetchingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/insights/summary`,
                {},
                { insights: validNarratives },
                "POST", false, false
            );

            if (isSuccess && data?.summary) {
                setSummary(data.summary);
                _summaryCache.set(currentHash, data.summary);
                lastHashRef.current = currentHash;
            } else {
                setError("Failed to generate summary");
            }
        } catch (err) {
            console.error("Summary fetch failed", err);
            setError("Failed to generate summary");
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [validNarratives, hasEnough]); // eslint-disable-line

    // Auto-trigger once all graphs are resolved and we have enough narratives
    useEffect(() => {
        if (!allResolved || !hasEnough) return;
        const currentHash = hashNarratives(validNarratives);
        if (currentHash === lastHashRef.current && summary !== null) return;
        fetchSummary(false);
    }, [allResolved, resolvedInsights]); // eslint-disable-line

    const refresh = () => fetchSummary(true);

    if (resolvedInsights.length === 0) return null;

    const pendingCount = totalGraphCount - resolvedInsights.length;

    return (
        <>
            <style>{`
                @keyframes esb-shimmer { 0%,100%{background-position:200% 0} 50%{background-position:-200% 0} }
                @keyframes esb-spin    { to { transform: rotate(360deg); } }
                @keyframes esb-fade-in { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:none} }
            `}</style>

            <div style={{
                marginBottom: 28,
                borderRadius: 16,
                border: "1.5px solid #ede9fe",
                background: "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 40%, #fefce8 100%)",
                boxShadow: "0 4px 24px rgba(124,58,237,0.07), 0 1px 4px rgba(124,58,237,0.04)",
                overflow: "hidden",
                animation: "esb-fade-in 0.4s ease both",
                fontFamily: "'DM Sans', system-ui, sans-serif",
            }}>
                {/* Accent stripe */}
                <div style={{ height: 3, background: "linear-gradient(90deg, #7c3aed, #a78bfa, #818cf8)", flexShrink: 0 }} />

                {/* Header */}
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 18px",
                    borderBottom: expanded ? "1px solid #ede9fe" : "none",
                    cursor: "pointer", userSelect: "none",
                }}
                    onClick={() => setExpanded(v => !v)}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <div style={{
                            width: 30, height: 30, borderRadius: 9,
                            background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "white", flexShrink: 0,
                            boxShadow: "0 2px 8px rgba(124,58,237,0.3)",
                        }}>
                            <IconSparkle />
                        </div>

                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.025em" }}>
                                    Executive Summary
                                </span>
                                {loading && (
                                    <span style={{ fontSize: 10, fontWeight: 600, color: "#7c3aed", background: "#ede9fe", borderRadius: 99, padding: "2px 8px", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em" }}>
                                        Generating…
                                    </span>
                                )}
                                {!loading && summary && !error && (
                                    <span style={{ fontSize: 10, fontWeight: 600, color: "#059669", background: "#d1fae5", borderRadius: 99, padding: "2px 8px", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em" }}>
                                        Ready
                                    </span>
                                )}
                                {pendingCount > 0 && !loading && !summary && (
                                    <span style={{ fontSize: 10, fontWeight: 600, color: "#d97706", background: "#fef3c7", borderRadius: 99, padding: "2px 8px", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em" }}>
                                        {pendingCount} pending…
                                    </span>
                                )}
                            </div>
                            <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "'DM Mono', monospace", letterSpacing: "0.03em", marginTop: 1 }}>
                                {validNarratives.length} graph{validNarratives.length !== 1 ? "s" : ""} · {tabName}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {(summary || error) && (
                            <button
                                onClick={e => { e.stopPropagation(); refresh(); }}
                                disabled={loading}
                                style={{
                                    display: "flex", alignItems: "center", gap: 5,
                                    padding: "4px 10px", borderRadius: 7,
                                    border: "1px solid #ddd6fe",
                                    background: loading ? "#f5f3ff" : "#faf5ff",
                                    color: loading ? "#a78bfa" : "#7c3aed",
                                    fontSize: 11, fontWeight: 600,
                                    fontFamily: "'DM Mono', monospace", letterSpacing: "0.05em",
                                    cursor: loading ? "not-allowed" : "pointer",
                                    transition: "all 0.15s", outline: "none",
                                }}
                                onMouseOver={e => { if (!loading) e.currentTarget.style.background = "#ede9fe"; }}
                                onMouseOut={e => { if (!loading) e.currentTarget.style.background = "#faf5ff"; }}
                            >
                                <IconRefresh spinning={loading} />
                                Regenerate
                            </button>
                        )}
                        <div style={{ color: "#a78bfa" }}>
                            <IconChevron open={expanded} />
                        </div>
                    </div>
                </div>

                {/* Body */}
                {expanded && (
                    <div style={{ padding: "18px 20px 16px" }}>

                        {loading && !summary && <SummarySkeleton />}

                        {!loading && !summary && !error && pendingCount > 0 && (
                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
                                <div style={{
                                    width: 14, height: 14, minWidth: 14, borderRadius: "50%",
                                    border: "2px solid #ddd6fe", borderTopColor: "#7c3aed",
                                    animation: "esb-spin 0.8s linear infinite", flexShrink: 0,
                                }} />
                                <span style={{ fontSize: 12.5, color: "#94a3b8", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                                    Waiting for {pendingCount} graph insight{pendingCount !== 1 ? "s" : ""} to resolve…
                                </span>
                            </div>
                        )}

                        {error && !loading && (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                    style={{ width: 14, height: 14, minWidth: 14, flexShrink: 0, display: "block" }}>
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                    <line x1="12" y1="9" x2="12" y2="13"/>
                                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                                </svg>
                                <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 500, fontFamily: "'DM Sans', system-ui, sans-serif", flex: 1 }}>
                                    {error}
                                </span>
                                <button onClick={refresh} style={{ background: "none", border: "none", fontSize: 11, fontWeight: 700, color: "#dc2626", cursor: "pointer", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em", textDecoration: "underline", textUnderlineOffset: 2 }}>
                                    Retry
                                </button>
                            </div>
                        )}

                        {loading && summary && (
                            <div style={{ position: "relative" }}>
                                <div style={{ opacity: 0.35, pointerEvents: "none" }}>
                                    {renderMarkdown(summary)}
                                </div>
                                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 12, color: "#7c3aed", fontFamily: "'DM Mono', monospace" }}>
                                    <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #ddd6fe", borderTopColor: "#7c3aed", animation: "esb-spin 0.8s linear infinite" }} />
                                    Regenerating…
                                </div>
                            </div>
                        )}

                        {summary && !loading && (
                            <div style={{ animation: "esb-fade-in 0.3s ease both" }}>
                                {renderMarkdown(summary)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default ExecSummaryBanner;